'use client';

import { useParams } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { fallbackLng } from '@/i18n/settings';

type TranslationFunction = (key: string, paramsOrDefault?: Record<string, string | number> | string) => string;

// Module-level cache to persist translations across component re-renders
const translationCache: Record<string, Record<string, any>> = {};
const loadingPromises: Record<string, Promise<Record<string, any>>> = {};

async function loadTranslationFile(lng: string, namespace: string): Promise<Record<string, any>> {
  const cacheKey = `${lng}/${namespace}`;

  // Return cached translations if available
  if (translationCache[cacheKey]) {
    return translationCache[cacheKey];
  }

  // If already loading, return the existing promise to avoid duplicate requests
  if (loadingPromises[cacheKey]) {
    return loadingPromises[cacheKey];
  }

  // Create a new loading promise
  loadingPromises[cacheKey] = (async () => {
    try {
      const data = await import(`@/i18n/locales/${lng}/${namespace}.json`);
      const translations = data.default || data;
      translationCache[cacheKey] = translations;
      return translations;
    } catch (error) {
      console.error(`Failed to load translations for ${lng}/${namespace}`, error);
      // Fallback to English
      if (lng !== fallbackLng) {
        try {
          const fallbackKey = `${fallbackLng}/${namespace}`;
          if (translationCache[fallbackKey]) {
            return translationCache[fallbackKey];
          }
          const fallback = await import(`@/i18n/locales/${fallbackLng}/${namespace}.json`);
          const translations = fallback.default || fallback;
          translationCache[fallbackKey] = translations;
          return translations;
        } catch (fallbackError) {
          console.error(`Failed to load fallback translations for ${fallbackLng}/${namespace}`, fallbackError);
        }
      }
      return {};
    } finally {
      delete loadingPromises[cacheKey];
    }
  })();

  return loadingPromises[cacheKey];
}

export function useClientTranslation(namespace: string = 'common', overrideLng?: string) {
  const params = useParams();
  const lng = overrideLng || (params?.lng as string) || fallbackLng;
  const cacheKey = `${lng}/${namespace}`;

  // Initialize state with cached translations if available (avoids initial empty state)
  const [translations, setTranslations] = useState<Record<string, any>>(
    () => translationCache[cacheKey] || {}
  );
  const [isLoading, setIsLoading] = useState(!translationCache[cacheKey]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const loadTranslations = async () => {
      // Check cache first
      if (translationCache[cacheKey]) {
        setTranslations(translationCache[cacheKey]);
        setIsLoading(false);
        return;
      }

      const data = await loadTranslationFile(lng, namespace);
      if (mountedRef.current) {
        setTranslations(data);
        setIsLoading(false);
      }
    };

    loadTranslations();

    return () => {
      mountedRef.current = false;
    };
  }, [lng, namespace, cacheKey]);

  const t: TranslationFunction = (key, paramsOrDefault) => {
    const keys = key.split('.');
    let value: any = translations;

    // Check if second argument is a default string or params object
    const isDefaultString = typeof paramsOrDefault === 'string';
    const defaultValue = isDefaultString ? paramsOrDefault : undefined;
    const params = isDefaultString ? undefined : paramsOrDefault;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Return default value if provided, otherwise the key
        if (defaultValue !== undefined) {
          return defaultValue;
        }
        // Only warn if translations are loaded (not during initial load)
        if (Object.keys(translations).length > 0) {
          console.warn(`Translation key not found: ${key} in ${lng}/${namespace}`);
        }
        return key; // Return key as fallback
      }
    }

    if (typeof value !== 'string') {
      if (defaultValue !== undefined) {
        return defaultValue;
      }
      if (Object.keys(translations).length > 0) {
        console.warn(`Translation value is not a string: ${key}`);
      }
      return key;
    }

    // Replace parameters like {{name}}, {{workspace}} (i18next format)
    if (params) {
      Object.entries(params).forEach(([param, replacement]) => {
        value = value.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), replacement);
      });
    }

    return value;
  };

  return { t, lng, isLoading };
}
